import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import JsonView from '../../components/JsonView';
import RouteMapPanel from '../../components/RouteMapPanel';
import StatusChip from '../../components/StatusChip';
import { useRouteExplainQuery, useRunRoutesQuery } from '../../services/routing/queries';
import {
  driverLabel,
  formatSeconds,
  formatWallMs,
  planEarnings,
  plannedSpanSeconds,
  skipReason
} from '../../utils/format';
import type { Decision } from '../../types/routing';

export { timeOrderedStops } from '../../components/RouteMapPanel';

const Tile = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Paper sx={{ p: 1.5 }}>
    <Typography variant="h6">{value}</Typography>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Paper>
);

const CandidatesTable = ({ decision }: { decision: Decision }) => {
  const candidates = decision.candidates ?? [];
  if (!candidates.length) return null;
  const columns = [...new Set(candidates.flatMap(candidate => Object.keys(candidate)))];
  return (
    <TableContainer sx={{ mb: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map(column => (
              <TableCell key={column}>{column}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {candidates.map((candidate, index) => (
            <TableRow key={index}>
              {columns.map(column => {
                const value = candidate[column];
                if (column === 'reason_code' && typeof value === 'string') {
                  const accepted = /ACCEPT|CHOSEN|OK/.test(value);
                  return (
                    <TableCell key={column}>
                      <Chip label={value} size="small" color={accepted ? 'success' : 'warning'} />
                    </TableCell>
                  );
                }
                return (
                  <TableCell key={column}>
                    {typeof value === 'object' && value !== null
                      ? JSON.stringify(value)
                      : String(value ?? '')}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const RouteDetailPage = () => {
  const { runId = '', routeId = '' } = useParams();
  const { data: routes, isLoading, error } = useRunRoutesQuery(runId, false);
  const { data: explain } = useRouteExplainQuery(runId, routeId);
  const [focusRideId, setFocusRideId] = useState<number | null>(null);

  if (isLoading) return <CircularProgress aria-label="loading route" />;
  if (error) return <Alert severity="error">{(error as Error).message}</Alert>;

  const route = (routes ?? []).find(row => String(row.route_id) === routeId);
  if (!route) {
    return <Alert severity="error">{`Route ${routeId} not found in run ${runId}`}</Alert>;
  }

  const plan = route.plan ?? [];
  const earnings = planEarnings(plan);
  const deadheadMiles = plan
    .map(ride => ride.deadhead_miles)
    .filter((value): value is number => value != null)
    .reduce((total, value) => total + value, 0);
  const routeSeconds = plannedSpanSeconds(route.planned_start_at, route.planned_end_at);


  return (
    <Stack spacing={2}>
      <Link component={RouterLink} to={`/runs/${runId}`} underline="hover">
        ← Run #{runId}
      </Link>

      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <Typography variant="h6">Route {route.route_id}</Typography>
          <StatusChip status={route.status} />
          <StatusChip status={route.outcome} />
          <Typography color="text.secondary">driver {driverLabel(route)}</Typography>
        </Stack>
        <Grid container spacing={1.5} sx={{ mb: 1 }}>
          <Grid item xs={6} sm={2}>
            <Tile label="rides" value={route.rides_assigned} />
          </Grid>
          <Grid item xs={6} sm={2}>
            <Tile label="pools" value={route.pools_assigned} />
          </Grid>
          <Grid item xs={6} sm={2}>
            <Tile label="engine calls" value={route.engine_calls} />
          </Grid>
          <Grid item xs={6} sm={2}>
            <Tile label="fallbacks" value={route.engine_fallbacks} />
          </Grid>
          <Grid item xs={6} sm={2}>
            <Tile
              label={
                earnings.counted === earnings.rides
                  ? 'route earns'
                  : `route earns (${earnings.counted}/${earnings.rides} rides)`
              }
              value={earnings.counted ? `$${earnings.total.toFixed(2)}` : '—'}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <Tile label="route time" value={formatSeconds(routeSeconds) || '—'} />
          </Grid>
          <Grid item xs={6} sm={2}>
            <Tile label="deadhead" value={`${deadheadMiles.toFixed(1)} mi`} />
          </Grid>
          <Grid item xs={6} sm={2}>
            <Tile label="wall time" value={formatWallMs(route.wall_ms) || '—'} />
          </Grid>
        </Grid>
        <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
          shift {route.route_from_datetime ?? '?'} → {route.route_to_datetime ?? '?'} UTC
          {route.planned_start_at
            ? ` · planned span ${route.planned_start_at} → ${route.planned_end_at}`
            : ''}
        </Typography>
        {route.error_code && (
          <Alert severity={skipReason(route) ? 'info' : 'error'} sx={{ mt: 1 }}>
            {skipReason(route) ? `skipped: ${skipReason(route)}` : route.error_code}
            {route.error_detail ? ` — ${route.error_detail}` : ''}
          </Alert>
        )}
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          PLANNED ROUTE ON MAP
        </Typography>
        <RouteMapPanel
          runId={runId}
          routeId={routeId}
          plan={plan}
          focusRideId={focusRideId}
          onClearFocus={() => setFocusRideId(null)}
        />
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          PLAN — ORDERED ASSIGNMENTS
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Ride</TableCell>
                <TableCell>Requested pickup</TableCell>
                <TableCell>Planned pickup</TableCell>
                <TableCell>Planned dropoff</TableCell>
                <TableCell>Pooled</TableCell>
                <TableCell align="right">Deadhead</TableCell>
                <TableCell align="right">Onboard</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plan.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography color="text.secondary">Empty plan.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {plan.map((ride, index) => (
                <TableRow
                  key={ride.ride_id}
                  hover
                  selected={ride.ride_id === focusRideId}
                  sx={{ cursor: 'pointer' }}
                  aria-label={`show ride ${ride.ride_id} on the map`}
                  onClick={() =>
                    setFocusRideId(current => (current === ride.ride_id ? null : ride.ride_id))
                  }
                >
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    {ride.ride_id}
                    {ride.reference_id ? ` (${ride.reference_id})` : ''}
                  </TableCell>
                  <TableCell>{ride.pickup_at ?? ''}</TableCell>
                  <TableCell>{ride.planned_pickup_at ?? ''}</TableCell>
                  <TableCell>{ride.planned_dropoff_at ?? ''}</TableCell>
                  <TableCell>
                    {ride.pooled && <Chip label="pooled" size="small" color="secondary" />}
                  </TableCell>
                  <TableCell align="right">
                    {ride.deadhead_miles != null
                      ? `${ride.deadhead_miles.toFixed(1)} mi / ${ride.deadhead_seconds} s`
                      : ''}
                  </TableCell>
                  <TableCell align="right">
                    {ride.onboard_minutes
                      ? `${ride.onboard_minutes} / ${ride.onboard_minutes_allowed} min`
                      : ''}
                  </TableCell>
                  <TableCell align="right">
                    {ride.amount != null ? `$${ride.amount.toFixed(2)}` : ''}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <JsonView title="Why is this route empty?" value={route.empty_outline} />
        <JsonView title="Pool outline" value={route.pool_outline} />
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          EXPLAIN — EVERY DECISION, CANDIDATE BY CANDIDATE
        </Typography>
        {!explain?.length && (
          <Typography color="text.secondary">
            No decisions recorded (30-day retention, or the route never ran).
          </Typography>
        )}
        {(explain ?? []).map(decision => (
          <Accordion key={decision.seq} defaultExpanded={decision.chosen_ride_id !== null}>
            <AccordionSummary>
              <Typography variant="body2">
                <b>{decision.phase}</b> · seq {decision.seq} ·{' '}
                {decision.chosen_ride_id !== null
                  ? `chose ride ${decision.chosen_ride_id}`
                  : 'no ride chosen'}{' '}
                · {decision.candidates_total} candidate
                {decision.candidates_total === 1 ? '' : 's'}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <CandidatesTable decision={decision} />
            </AccordionDetails>
          </Accordion>
        ))}
      </Paper>
    </Stack>
  );
};

export default RouteDetailPage;
